import { guardString } from "@msuite/utils/strings";
import { getFullName } from "@msuite/utils/users";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";

/**
 * Initialize Sentry with the DSN from environment variables.
 * The DSN is used to send error reports to Sentry. Only the production build reports:
 * `vite` dev serves `NODE_ENV=development`, the forge build defines `production`.
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 */
Sentry.init({
	dsn: process.env.PUBLIC_SENTRY_DSN,
	sendDefaultPii: true,
	enabled: process.env.NODE_ENV === "production",
});

/**
 * This hook sets the Sentry user context based on the current user and organization.
 * It updates the Sentry user context whenever the user or organization changes.
 * It includes the user's ID, email, and full name in the Sentry user context.
 * @see https://docs.sentry.io/platforms/javascript/guides/react/configuration/user-context/
 */
export function useSentryUser() {
	/** Context */
	const { organization, user } = useAuth();

	/** Effects */
	useEffect(() => {
		if (!user || !organization) return;
		Sentry.setUser({
			userId: user.id,
			orgId: organization.id,
			email: guardString(user.email),
			fullName: getFullName(user),
		});
	}, [user, organization]);
}
